import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import {
  getBaseUrl,
  normalizeChatbotPosition,
  type ChatbotPosition,
} from '@omq/shared';
import { ChatBubble } from './ChatBubble';
import { ChatWindow } from './ChatWindow';
import { Message } from './MessageList';
import { FileAttachment } from './MessageInput';
import { MultiSelectOption } from './MultiSelectMessage';
import { ChatbotCallbacks, ChatbotControls } from '../chatbot';
import { chatbotStorageFor } from './sessionStorage';
import { resolveProactiveDelayMs } from './proactiveDelay';
import type { ChatbotDisplayMode } from './displayMode';

interface PresignResponse {
  uploadUrl: string;
  fileUrl: string;
  fileKey: string;
  expiresIn: number;
}

interface ContactInfo {
  name?: string;
  email?: string;
  mobileNumber?: string;
  externalId?: string;
  customFields?: Record<string, unknown>;
}

interface ConversationInfo {
  subject?: string;
  locale?: string;
}

interface ChatContainerProps {
  account: string;
  apiKey: string;
  // Position set explicitly by the integrator (config or data-attribute).
  // When undefined, falls back to the backend value (chatConfig.bubblePosition),
  // then to 'right'.
  explicitPosition?: ChatbotPosition;
  customValues?: Record<string, string | number | boolean>;
  autoOpen?: boolean;
  testMode?: boolean;
  // Native hover tooltip for the collapsed bubble button (snippet-driven).
  bubbleTitle?: string;
  // 'embedded' renders the window inline and always open: no bubble, and
  // open/close are no-ops. Defaults to 'floating'.
  displayMode?: ChatbotDisplayMode;
  callbacks?: ChatbotCallbacks;
  controls?: Partial<ChatbotControls>;
  shadowRoot?: ShadowRoot;
}

// Delay before the mobile host-content mask is applied on open. Matches the shell
// open morph duration in chatbot.less (.omq-chatbot-shell transition, 350ms) so the
// host page stays visible behind the growing window and is only hidden once the
// window has expanded to fullscreen — applying the mask at the start of the morph
// made the host page appear to blink out of existence.
const MOBILE_MASK_DELAY_MS = 350;

type BubbleState = 'bubble' | 'card' | 'open';

/**
 * Splits a CSS string into its `@font-face` blocks and everything else.
 *
 * `@font-face` declared inside a Shadow DOM does not reliably register the
 * font with the host document's font manager — elements inside the same
 * shadow tree compute the correct `font-family` value but the browser never
 * triggers the underlying font fetch. To fix this we extract `@font-face`
 * blocks from the customer CSS and inject them into the host document's
 * light DOM, while keeping the rest of the CSS scoped to the shadow root.
 *
 * `@font-face` blocks contain only descriptors (no nested selectors), so a
 * simple brace-depth scan suffices.
 *
 * @param css combined CSS string to split
 * @returns object with `fontFaces` (joined @font-face blocks) and `rest`
 */
const splitFontFaces = (css: string): { fontFaces: string; rest: string } => {
  const fontFaces: string[] = [];
  const rest: string[] = [];
  let i = 0;
  while (i < css.length) {
    const start = css.indexOf('@font-face', i);
    if (start === -1) {
      rest.push(css.slice(i));
      break;
    }
    rest.push(css.slice(i, start));
    const blockStart = css.indexOf('{', start);
    if (blockStart === -1) {
      // malformed — bail and treat remainder as rest so we don't strand it in document.head
      rest.push(css.slice(start));
      break;
    }
    let depth = 1;
    let j = blockStart + 1;
    while (depth > 0 && j < css.length) {
      if (css[j] === '{') depth++;
      else if (css[j] === '}') depth--;
      j++;
    }
    if (depth !== 0) {
      // unbalanced — keep remainder in rest rather than poisoning document.head
      rest.push(css.slice(start));
      break;
    }
    fontFaces.push(css.slice(start, j));
    i = j;
  }
  return { fontFaces: fontFaces.join('\n\n'), rest: rest.join('') };
};

/**
 * Stable element id for the document-level `<style>` that holds extracted
 * `@font-face` declarations. One per chatbot account so multiple chatbot
 * instances on the same host page do not clobber each other.
 *
 * @param account account identifier
 * @returns element id
 */
const documentFontStyleId = (account: string): string =>
  `omq-chatbot-font-faces-${account}`;

/**
 * ChatContainer component
 * Main container that orchestrates the chat bubble, window, and socket connection
 */
export const ChatContainer: React.FC<ChatContainerProps> = ({
  account,
  apiKey,
  explicitPosition,
  customValues,
  autoOpen = false,
  testMode,
  bubbleTitle,
  displayMode = 'floating',
  callbacks,
  controls,
  shadowRoot,
}) => {
  const embedded = displayMode === 'embedded';
  // Browser storage scoped to this chat client (see sessionStorage.ts). Memoised
  // so the object identity is stable across renders and can sit in dependency arrays.
  const storage = useMemo(() => chatbotStorageFor(apiKey), [apiKey]);
  // Evaluated once per mount: clears pre-cookie localStorage even if the chat is
  // never opened, then decides whether this page load reconnects to a stored session.
  const [hasStoredSession] = useState(() => {
    storage.migrateLegacyStorage();
    return !!storage.readSession();
  });
  const [bubbleState, setBubbleState] = useState<BubbleState>(
    embedded || autoOpen ? 'open' : 'bubble',
  );
  // Gates the greeting typewriter: only true once the proactive card has finished
  // its open/grow animation, so the text starts typing after the card has fully
  // expanded (not while it is still morphing in).
  const [proactiveTyping, setProactiveTyping] = useState(false);
  // isOpen is derived: the chat is "open" exactly when the shell is in the open state.
  const isOpen = bubbleState === 'open';
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [quickResponses, setQuickResponses] = useState<string[]>([]);
  const [multiSelectOptions, setMultiSelectOptions] = useState<MultiSelectOption[]>([]);
  const [multiSelectMinMax, setMultiSelectMinMax] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
  const [multiSelectValidateErrorMessage, setMultiSelectValidateErrorMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [activityText, setActivityText] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSessionEnded, setIsSessionEnded] = useState(false);
  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [uploadAccept, setUploadAccept] = useState<string | null>(null);
  const [uploadMultiple, setUploadMultiple] = useState(false);
  const [uploadValidateError, setUploadValidateError] = useState<string | null>(null);
  const [inputFieldDescription, setInputFieldDescription] = useState<string | null>(null);
  const [inputFieldPlaceholder, setInputFieldPlaceholder] = useState<string | null>(null);
  const [multiSelectDescription, setMultiSelectDescription] = useState<string | null>(null);
  const [quickResponsesDescription, setQuickResponsesDescription] = useState<string | null>(null);
  const [uploadDescription, setUploadDescription] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [conversationInfo, setConversationInfo] = useState<ConversationInfo | null>(null);
  const [relayUrl, setRelayUrl] = useState<string | null>(null);
  const [configReady, setConfigReady] = useState(false);
  const [chatConfig, setChatConfig] = useState<{
    messages?: Record<string, string>;
    relayUrl?: string;
    bubblePosition?: string;
    logoUrl?: string | null;
    botName?: string | null;
    proactiveMode?: boolean;
    // Delay (ms) before the bubble morphs into the proactive card. Absent on
    // older backends; resolveProactiveDelayMs() supplies the fallback.
    proactiveDelayMs?: number;
    language?: string;
  } | null>(null);
  const socketRef = useRef<Socket | null>(null);
  // Session the socket offers the relay on each (re)connect, kept current by the session events.
  const sessionIdRef = useRef<string | null>(null);
  const pendingDelaysRef = useRef<NodeJS.Timeout[]>([]);
  const reconnectingRef = useRef(hasStoredSession);
  const presignPromiseRef = useRef<{
    resolve: (value: PresignResponse) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const keepaliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const restartTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasEstablishedSessionRef = useRef(false);
  const pendingDescriptionRef = useRef<string | null>(null);
  const proactiveShownRef = useRef(false);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const proactiveStartSizeRef = useRef<{ w: number; h: number } | null>(null);
  // Captured card footprint when opening the chat FROM the proactive card, so the
  // card→window morph can grow from it (the auto-sized card can't be CSS-transitioned).
  const cardOpenStartSizeRef = useRef<{ w: number; h: number } | null>(null);

  /**
   * Extends the stored session's lifetime while the session is in use, so it is
   * forgotten one lifetime after the visitor's last activity. Never writes once
   * the session has ended or been cleared here (the in-memory id is null then),
   * nor when another tab has ended or replaced it (see ChatbotStorage.renewSession).
   */
  const renewStoredSession = useCallback(() => {
    if (sessionIdRef.current) {
      storage.renewSession(sessionIdRef.current);
    }
  }, [storage]);

  // Resolution order: explicit (config/data-attribute) → backend → fallback 'right'.
  const effectivePosition: ChatbotPosition =
    explicitPosition
    ?? normalizeChatbotPosition(chatConfig?.bubblePosition)
    ?? 'right';

  /**
   * Fetch chat client configuration (including relay URL and style) from the backend
   */
  const fetchConfig = useCallback(async () => {
    try {
      const baseUrl = getBaseUrl(account) || '';
      const response = await fetch(`${baseUrl}/api/chat_bot/omq/init?api_key=${apiKey}`, {
        method: 'POST',
      });
      if (!response.ok) {
        console.warn('[Chatbot] Failed to fetch config:', response.status);
        return;
      }
      const config = await response.json();
      setChatConfig(config);
      if (config.relayUrl) {
        setRelayUrl(config.relayUrl);
      }

      // Inject custom style CSS. @font-face blocks go into the host document's
      // light DOM because @font-face declared inside a Shadow DOM does not
      // reliably register the font with the document font manager. Everything
      // else stays scoped to the shadow root.
      if (config.style && shadowRoot) {
        const { fontFaces, rest } = splitFontFaces(config.style);

        const docFontStyleId = documentFontStyleId(account);
        document.getElementById(docFontStyleId)?.remove();
        if (fontFaces.trim()) {
          const docStyleEl = document.createElement('style');
          docStyleEl.id = docFontStyleId;
          docStyleEl.textContent = fontFaces;
          document.head.appendChild(docStyleEl);
        }

        shadowRoot.querySelector('#omq-chatbot-custom-style')?.remove();
        const styleEl = document.createElement('style');
        styleEl.id = 'omq-chatbot-custom-style';
        styleEl.textContent = rest;
        shadowRoot.appendChild(styleEl);
      }
    } catch (error) {
      console.error('[Chatbot] Failed to fetch config:', error);
    } finally {
      setConfigReady(true);
    }
  }, [account, apiKey, shadowRoot]);

  /**
   * Reset all visible conversation UI to a "fresh conversation" state.
   *
   * Wipes messages, interactive widgets (quick responses, multi-select,
   * upload zone), descriptions/placeholders, typing/activity indicators,
   * contact/conversation info, and clears any pending display timeouts.
   *
   * Does NOT touch sessionId, isSessionEnded, isConnected, stored session,
   * socket lifecycle, or isOpen — callers decide those because they differ
   * per call site.
   */
  const resetConversationUiState = useCallback(() => {
    pendingDelaysRef.current.forEach((timeout) => clearTimeout(timeout));
    pendingDelaysRef.current = [];

    setMessages([]);
    setQuickResponses([]);
    setQuickResponsesDescription(null);
    setMultiSelectOptions([]);
    setMultiSelectMinMax({ min: 0, max: 0 });
    setMultiSelectValidateErrorMessage(null);
    setMultiSelectDescription(null);
    setUploadEnabled(false);
    setIsUploading(false);
    setUploadAccept(null);
    setUploadMultiple(false);
    setUploadValidateError(null);
    setUploadDescription(null);
    setUploadError(null);
    setInputFieldDescription(null);
    setInputFieldPlaceholder(null);
    setIsTyping(false);
    setActivityText(null);
    setContactInfo(null);
    setConversationInfo(null);
    pendingDescriptionRef.current = null;
  }, []);

  /**
   * Initialize socket connection
   */
  const initializeSocket = useCallback(() => {
    if (!relayUrl) {
      console.warn('[Chatbot] Cannot connect: relay URL not yet loaded');
      return null;
    }

    sessionIdRef.current = storage.readSession();

    // Reset — will be set to true once a session is established.
    // This distinguishes fresh page loads from socket.io auto-reconnects.
    hasEstablishedSessionRef.current = false;

    console.info('[Chatbot] Connecting to:', relayUrl);

    const socket = io(relayUrl, {
      // A callback, not an object: socket.io resends auth on every reconnect, and an object
      // would keep offering the page-load session instead of the current one (#2896).
      auth: (cb) =>
        cb({
          sessionId: sessionIdRef.current || undefined,
          account,
          apiKey,
          ...(customValues && { customValues }),
          ...(testMode && { testMode: true }),
        }),
      withCredentials: true,
    });

    socketRef.current = socket;

    // Connection events
    socket.on('connect', () => {
      console.info('[Chatbot] Connected');
      setIsConnected(true);

      // Start keepalive pings to prevent session expiry on the relay server.
      // The relay resets its session TTL on each ping event; the stored session
      // is renewed alongside it so both expire together.
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
      }
      keepaliveIntervalRef.current = setInterval(() => {
        if (socket.connected) {
          socket.emit('ping');
          renewStoredSession();
        }
      }, 2 * 60 * 1000); // every 2 minutes

      // Send account credentials to server
      socket.emit('init', {
        account,
        apiKey,
        ...(customValues && { customValues }),
      });
    });

    socket.on('disconnect', (reason) => {
      console.info('[Chatbot] Disconnected:', reason);
      setIsConnected(false);

      // Stop keepalive — it will restart on next connect
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = null;
      }
    });

    // Session events
    socket.on('session_created', (data: { sessionId: string }) => {
      console.info('[Chatbot] Session created:', data.sessionId);

      if (sessionIdRef.current) {
        // We attempted to restore (auth.sessionId was sent) but the relay minted
        // a new session — the prior conversation is server-side gone. Wipe the
        // visible UI so the new greeting lands in an empty conversation that
        // matches server state. Visual no-op on a fresh page load (state already
        // empty); cleans up the in-tab auto-reconnect case where the user sees
        // the stale conversation today.
        if (hasEstablishedSessionRef.current) {
          console.info(
            '[Chatbot] Reconnect produced a new sessionId; clearing stale UI'
          );
        }
        resetConversationUiState();
      }

      setSessionId(data.sessionId);
      sessionIdRef.current = data.sessionId;
      storage.saveSession(data.sessionId);
      hasEstablishedSessionRef.current = true;
      reconnectingRef.current = false;
    });

    socket.on(
      'session_restored',
      (data: {
        sessionId: string;
        context?: {
          chatMessages?: Array<{ type: string; text: string; timestamp?: number; variant?: string }>;
          uploadState?: {
            enabled: boolean;
            accept?: string | null;
            multiple?: boolean;
            validateError?: string | null;
            description?: string | null;
          };
          quickResponses?: string[];
          quickResponsesDescription?: string | null;
          multiSelectState?: {
            document?: string;
            options: MultiSelectOption[];
            minSelect?: number;
            maxSelect?: number;
            validateErrorMessage?: string | null;
          };
          inputFieldState?: {
            description?: string | null;
            placeholder?: string | null;
          };
        };
      }) => {
        console.info('[Chatbot] Session restored:', data.sessionId, data.context);
        setSessionId(data.sessionId);
        sessionIdRef.current = data.sessionId;
        storage.renewSession(data.sessionId);
        hasEstablishedSessionRef.current = true;

        // Auto-open the chat window when reconnecting to an existing session,
        // but respect the user's explicit close action
        if (reconnectingRef.current) {
          if (!storage.isUserClosed()) {
            setBubbleState('open');
          }
          reconnectingRef.current = false;
        }

        // Restore messages from context - convert server format to our Message format
        if (data.context?.chatMessages && data.context.chatMessages.length > 0) {
          const convertedMessages: Message[] = data.context.chatMessages.map((msg, index) => ({
            id: `restored-${index}-${Date.now()}`,
            type: msg.type === 'user' ? 'sent' : 'received',
            content: msg.text,
            timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
            isHtml: msg.type !== 'user',
            variant: msg.variant === 'html-snippet' ? 'html-snippet' : undefined,
          }));
          setMessages(convertedMessages);
        }

        // Restore upload UI state if the last interaction was an upload request
        if (data.context?.uploadState?.enabled) {
          setUploadEnabled(true);
          setUploadAccept(data.context.uploadState.accept ?? null);
          setUploadMultiple(data.context.uploadState.multiple ?? false);
          setUploadValidateError(data.context.uploadState.validateError ?? null);
          setUploadDescription(data.context.uploadState.description ?? null);
          pendingDescriptionRef.current = data.context.uploadState.description ?? null;
        }

        // Restore quick response buttons
        if (data.context?.quickResponses && data.context.quickResponses.length > 0) {
          setQuickResponses(data.context.quickResponses);
          const desc = data.context.quickResponsesDescription ?? null;
          setQuickResponsesDescription(desc);
          pendingDescriptionRef.current = desc;
        }

        // Restore multi-select UI state if the last interaction was a multi-select prompt
        if (data.context?.multiSelectState?.options && data.context.multiSelectState.options.length > 0) {
          setMultiSelectOptions(data.context.multiSelectState.options);
          setMultiSelectMinMax({
            min: data.context.multiSelectState.minSelect ?? 0,
            max: data.context.multiSelectState.maxSelect ?? 0,
          });
          setMultiSelectValidateErrorMessage(data.context.multiSelectState.validateErrorMessage ?? null);
          const desc = data.context.multiSelectState.document ?? null;
          setMultiSelectDescription(desc);
          pendingDescriptionRef.current = desc;
        }

        // Restore input field state if the last interaction was a free-text input prompt
        if (data.context?.inputFieldState) {
          const desc = data.context.inputFieldState.description ?? null;
          const placeholder = data.context.inputFieldState.placeholder ?? null;
          setInputFieldDescription(desc);
          setInputFieldPlaceholder(placeholder);
          pendingDescriptionRef.current = desc;
        }
      },
    );

    // API response events — receives protocol Data[]
    socket.on(
      'api_response',
      (data: {
        data?: Array<{
          sessionId: string;
          packet: {
            name: string;
            payload: Record<string, unknown>;
          };
        }>;
      }) => {
        console.info('[Chatbot] API Response:', data);

        // Clear any pending delays
        pendingDelaysRef.current.forEach((timeout) => clearTimeout(timeout));
        pendingDelaysRef.current = [];

        // Remove quick responses, upload zone, and multi-select when new response comes
        setQuickResponses([]);
        setMultiSelectOptions([]);
        setMultiSelectMinMax({ min: 0, max: 0 });
        setMultiSelectValidateErrorMessage(null);
        setUploadEnabled(false);
        setUploadAccept(null);
        setUploadMultiple(false);
        setUploadValidateError(null);
        setMultiSelectDescription(null);
        setQuickResponsesDescription(null);
        setUploadDescription(null);
        setInputFieldDescription(null);
        setInputFieldPlaceholder(null);
        pendingDescriptionRef.current = null;

        if (data.data && Array.isArray(data.data)) {
          let cumulativeDelay = 0;
          const items = data.data;

          items.forEach((item, index) => {
            const { name, payload } = item.packet;

            // Extract delay from payload (message and choices packets have it)
            const itemDelay = ((payload.delay as number) || 0) * 1000;
            cumulativeDelay += itemDelay;

            const isFirstItem = index === 0;
            const isLastItem = index === items.length - 1;

            const displayItem = () => {
              if (isFirstItem) {
                setIsTyping(false);
                setActivityText(null);
              }

              // Handle message — display as chat bubble
              if (name === 'message') {
                const document = payload.document as string | undefined;
                if (document) {
                  const newMessage: Message = {
                    id: `msg-${Date.now()}-${index}`,
                    type: 'received',
                    content: document,
                    timestamp: new Date(),
                    isHtml: true,
                  };
                  setMessages((prev) => [...prev, newMessage]);
                }
              }

              // Handle html_snippet — display as a full-width block
              if (name === 'html_snippet') {
                const html = payload.html as string | undefined;
                if (html) {
                  const newMessage: Message = {
                    id: `snippet-${Date.now()}-${index}`,
                    type: 'received',
                    content: html,
                    timestamp: new Date(),
                    isHtml: true,
                    variant: 'html-snippet',
                  };
                  setMessages((prev) => [...prev, newMessage]);
                }
              }

              // Handle choices — show quick response buttons with optional description
              if (name === 'choices') {
                const document = (payload.document as string | undefined) ?? null;
                const placeholder = (payload.placeholder as string | undefined) ?? null;
                if (isLastItem) {
                  const answerChoices = payload.answerChoices as string[] | undefined;
                  if (answerChoices && answerChoices.length > 0) {
                    setQuickResponses(answerChoices);
                    setQuickResponsesDescription(document);
                    setInputFieldPlaceholder(placeholder);
                    pendingDescriptionRef.current = document;
                  }
                }
              }

              // Show input field UI when backend requests free-text input
              if (name === 'input_field') {
                const description = (payload.description as string | null) ?? null;
                const placeholder = (payload.placeholder as string | null) ?? null;
                setInputFieldDescription(description);
                setInputFieldPlaceholder(placeholder);
                pendingDescriptionRef.current = description;
              }

              // Handle navigate
              if (name === 'navigate') {
                const url = payload.url as string | undefined;
                if (url) {
                  // Auto-navigate for same-domain URLs
                  let isSafeSameDomain = false;
                  try {
                    const targetUrl = new URL(url, window.location.origin);
                    const isSafeProtocol = targetUrl.protocol === 'https:' || targetUrl.protocol === 'http:';
                    isSafeSameDomain = isSafeProtocol && targetUrl.hostname === window.location.hostname;
                  } catch {
                    isSafeSameDomain = url.startsWith('/');
                  }

                  if (isSafeSameDomain) {
                    if (callbacks?.onNavigate) {
                      const result = callbacks.onNavigate(url);
                      if (result === false) return;
                    }
                    storage.markAutoOpen();
                    window.location.href = url;
                  } else {
                    const newMessage: Message = {
                      id: `msg-${Date.now()}-${index}`,
                      type: 'received',
                      content: '',
                      timestamp: new Date(),
                      isHtml: true,
                      navigateUrl: url,
                    };
                    setMessages((prev) => [...prev, newMessage]);
                  }
                }
              }

              // Handle end session
              if (name === 'end') {
                setIsSessionEnded(true);
              }

              // Handle contact info
              if (name === 'contact_update') {
                setContactInfo((payload as unknown) as ContactInfo);
              }

              // Handle conversation info
              if (name === 'conversation_update') {
                setConversationInfo((payload as unknown) as ConversationInfo);
              }

              // Show upload zone when backend requests it (with optional description inside)
              if (name === 'request_upload') {
                const description = (payload.description as string | null) ?? null;
                const placeholder = (payload.placeholder as string | undefined) ?? null;
                setUploadEnabled(true);
                setUploadAccept((payload.accept as string | null) ?? null);
                setUploadMultiple((payload.multiple as boolean) ?? false);
                setUploadValidateError((payload.validateErrorMessage as string | null) ?? null);
                setUploadDescription(description);
                setInputFieldPlaceholder(placeholder);
                pendingDescriptionRef.current = description;
              }

              // Handle multi_select — display multi-select UI with description inside it
              if (name === 'multi_select') {
                const document = (payload.document as string | undefined) ?? null;
                const placeholder = (payload.placeholder as string | undefined) ?? null;
                const options = payload.options as MultiSelectOption[] | undefined;

                if (options && options.length > 0) {
                  const minSelect = (payload.minSelect as number) || 0;
                  const maxSelect = (payload.maxSelect as number) || 0;
                  setMultiSelectMinMax({ min: minSelect, max: maxSelect });
                  setMultiSelectValidateErrorMessage((payload.validateErrorMessage as string | null) ?? null);
                  setMultiSelectOptions(options);
                  setMultiSelectDescription(document);
                  setInputFieldPlaceholder(placeholder);
                  pendingDescriptionRef.current = document;
                }
              }
            };

            if (cumulativeDelay === 0) {
              displayItem();
            } else {
              const timeout = setTimeout(displayItem, cumulativeDelay);
              pendingDelaysRef.current.push(timeout);
            }
          });

          if (items.length === 0) {
            setIsTyping(false);
            setActivityText(null);
          }
        } else {
          setIsTyping(false);
          setActivityText(null);
        }
      },
    );

    socket.on('activity_indicator', (data: { text: string }) => {
      setActivityText(data.text);
    });

    socket.on('api_error', (error: { error: string; circuitOpen?: boolean }) => {
      console.error('[Chatbot] API Error:', error);
      // Circuit breaker errors are suppressed — the backend response
      // still arrives via the push channel (api_response event).
      if (error.circuitOpen) {
        return;
      }
      setIsTyping(false);
      setActivityText(null);
      const errorMessage: Message = {
        id: `msg-error-${Date.now()}`,
        type: 'system',
        content: `Error: ${error.error}`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    });

    socket.on('error', (error: { message?: string }) => {
      console.error('[Chatbot] Socket Error:', error);
      const errorMessage: Message = {
        id: `msg-error-${Date.now()}`,
        type: 'system',
        content: `Error: ${error.message || 'Unknown error'}`,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    });

    // Presigned URL response
    socket.on('presign_response', (data: PresignResponse) => {
      console.info('[Chatbot] Presign response:', data);
      if (presignPromiseRef.current) {
        presignPromiseRef.current.resolve(data);
        presignPromiseRef.current = null;
      }
    });

    // Presigned URL error
    socket.on('presign_error', (error: { error: string }) => {
      console.error('[Chatbot] Presign error:', error);
      if (presignPromiseRef.current) {
        presignPromiseRef.current.reject(new Error(error.error));
        presignPromiseRef.current = null;
      }
    });

    // Session ended event — clean up and disconnect to prevent
    // socket.io from auto-reconnecting and creating new sessions.
    socket.on('session_ended', (data: { sessionId: string; timestamp: number }) => {
      console.info('[Chatbot] Session ended:', data.sessionId);
      setIsSessionEnded(true);
      storage.clearSession();
      setSessionId(null);
      sessionIdRef.current = null;
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = null;
      }
      socket.disconnect();
      setIsConnected(false);
    });

    return socket;
  }, [relayUrl, account, apiKey, customValues, testMode, resetConversationUiState, storage, renewStoredSession]);

  /**
   * Request a presigned URL for S3 upload via WebSocket
   */
  const requestPresignedUrl = useCallback(
    (file: File): Promise<PresignResponse> => {
      return new Promise((resolve, reject) => {
        if (!socketRef.current || !isConnected) {
          reject(new Error('Not connected'));
          return;
        }

        // Store promise handlers
        presignPromiseRef.current = { resolve, reject };

        // Request presigned URL via WebSocket
        socketRef.current.emit('request_upload_url', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });

        // Timeout after 30 seconds
        setTimeout(() => {
          if (presignPromiseRef.current) {
            presignPromiseRef.current.reject(new Error('Presign request timeout'));
            presignPromiseRef.current = null;
          }
        }, 30000);
      });
    },
    [isConnected],
  );

  /**
   * Upload file directly to S3 using presigned URL with retry logic
   */
  const uploadToS3 = useCallback(
    async (file: File, uploadUrl: string, maxRetries = 3): Promise<void> => {
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          if (!response.ok) {
            throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
          }

          return; // Success
        } catch (error) {
          lastError = error instanceof Error ? error : new Error(String(error));
          console.warn(`[Chatbot] S3 upload attempt ${attempt} failed:`, lastError.message);

          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, attempt - 1) * 1000;
            await new Promise((r) => setTimeout(r, delay));
          }
        }
      }

      throw lastError || new Error('Upload failed after retries');
    },
    [],
  );

  /**
   * Append the pending inline description (if any) as a received bubble in the
   * transcript, then clear it. Called when the user submits via text input or
   * confirms a multi-select, so the description becomes part of history.
   */
  const consumePendingDescription = useCallback(() => {
    const desc = pendingDescriptionRef.current;
    if (!desc) {
      return;
    }
    const descMessage: Message = {
      id: `msg-prompt-${Date.now()}`,
      type: 'received',
      content: desc,
      timestamp: new Date(),
      isHtml: true,
    };
    setMessages((prev) => [...prev, descMessage]);
    pendingDescriptionRef.current = null;
    setInputFieldDescription(null);
    setInputFieldPlaceholder(null);
    setMultiSelectDescription(null);
    setQuickResponsesDescription(null);
    setUploadDescription(null);
  }, []);

  /**
   * Send a message (with optional file attachment via S3)
   */
  const sendMessage = useCallback(
    async (text: string, fileAttachments?: FileAttachment[]) => {
      if (!socketRef.current || !isConnected) {
        console.warn('[Chatbot] Cannot send message: not connected');
        return;
      }

      setUploadError(null);

      // Uploaded file metadata collected from S3 uploads
      const uploadedFiles: Array<{ fileUrl: string; fileMimeType: string; fileName: string }> = [];

      // Handle file uploads via S3
      if (fileAttachments && fileAttachments.length > 0) {
        setIsUploading(true);

        try {
          for (const attachment of fileAttachments) {
            // 1. Request presigned URL
            const presignResponse = await requestPresignedUrl(attachment.file);

            // 2. Upload to S3
            await uploadToS3(attachment.file, presignResponse.uploadUrl);

            // 3. Collect uploaded file metadata
            uploadedFiles.push({
              fileUrl: presignResponse.fileUrl,
              fileMimeType: attachment.file.type,
              fileName: attachment.file.name,
            });
          }
        } catch (error) {
          console.error('[Chatbot] File upload failed:', error);
          setIsUploading(false);
          setUploadError(error instanceof Error ? error.message : 'Upload failed');

          const errorMessage: Message = {
            id: `msg-error-${Date.now()}`,
            type: 'system',
            content: `File upload failed: ${
              error instanceof Error ? error.message : 'Unknown error'
            }. Please try again.`,
            timestamp: new Date(),
            isError: true,
          };
          setMessages((prev) => [...prev, errorMessage]);
          return;
        }

        setIsUploading(false);
      }

      consumePendingDescription();

      // Add sent message to list
      const sentMessage: Message = {
        id: `msg-sent-${Date.now()}`,
        type: 'sent',
        content: text,
        timestamp: new Date(),
        attachments:
          uploadedFiles.length > 0
            ? uploadedFiles.map((f) => ({
                path: f.fileUrl,
                mimeType: f.fileMimeType,
                name: f.fileName,
              }))
            : undefined,
      };
      setMessages((prev) => [...prev, sentMessage]);

      // Clear quick responses and upload zone
      setQuickResponses([]);
      setUploadEnabled(false);
      setUploadAccept(null);
      setUploadMultiple(false);
      setUploadValidateError(null);

      // Show typing indicator
      setIsTyping(true);

      // Build payload — single-file fields for backward compat, plus files array
      const firstFile = uploadedFiles.length > 0 ? uploadedFiles[0] : null;

      socketRef.current.emit('chat_message', {
        requestId: `chat-${Date.now()}`,
        action: 'nothing',
        payload: {
          text: text,
          sessionId: sessionId,
          timestamp: Date.now(),
          pageUrl: window.location.href,
          ...(firstFile && {
            fileUrl: firstFile.fileUrl,
            fileMimeType: firstFile.fileMimeType,
            fileName: firstFile.fileName,
          }),
          ...(uploadedFiles.length > 1 && {
            files: uploadedFiles,
          }),
        },
      });
    },
    [isConnected, sessionId, requestPresignedUrl, uploadToS3, consumePendingDescription],
  );

  /**
   * Handle quick response click
   */
  const handleQuickResponse = useCallback(
    (text: string) => {
      sendMessage(text);
    },
    [sendMessage],
  );

  /**
   * Handle multi-select confirmation
   * Sends the selected values as a JSON array string and adds a sent message
   * showing the human-readable labels.
   */
  const handleMultiSelectConfirm = useCallback(
    (selectedValues: string[]) => {
      if (!socketRef.current || !isConnected) {
        return;
      }

      // Resolve human-readable labels for the sent message bubble
      const labels = multiSelectOptions
        .filter((opt) => selectedValues.includes(opt.value))
        .map((opt) => opt.label)
        .join(', ');

      // Clear multi-select UI immediately
      setMultiSelectOptions([]);
      setMultiSelectMinMax({ min: 0, max: 0 });
      setMultiSelectValidateErrorMessage(null);

      consumePendingDescription();

      // Add user's selection as a sent message
      const sentMessage: Message = {
        id: `msg-${Date.now()}`,
        type: 'sent',
        content: labels,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, sentMessage]);

      // Show typing indicator
      setIsTyping(true);

      // Send JSON array string via chat_message (same mechanism as regular text)
      socketRef.current.emit('chat_message', {
        requestId: `chat-${Date.now()}`,
        action: 'nothing',
        payload: {
          text: JSON.stringify(selectedValues),
          sessionId: sessionId,
          timestamp: Date.now(),
          pageUrl: window.location.href,
        },
      });
    },
    [isConnected, sessionId, multiSelectOptions, consumePendingDescription],
  );

  /**
   * Handle navigation to another page.
   * If an onNavigate callback is set and returns false, the default navigation is prevented.
   */
  const handleNavigate = useCallback((url: string) => {
    if (callbacks?.onNavigate) {
      const result = callbacks.onNavigate(url);
      if (result === false) return;
    }
    try {
      // Only allow http/https navigation to prevent protocol-based attacks
      const targetUrl = new URL(url, window.location.origin);
      if (targetUrl.protocol !== 'https:' && targetUrl.protocol !== 'http:') {
        return;
      }
      // Set auto-open flag before navigating
      storage.markAutoOpen();
      window.location.href = url;
    } catch (e) {
      console.error('[Chatbot] Navigation failed:', e);
    }
  }, [callbacks, storage]);

  /**
   * Clear session and start fresh
   */
  const clearSession = useCallback(() => {
    storage.clearSession();
    sessionIdRef.current = null;

    resetConversationUiState();
    setSessionId(null);
    setIsSessionEnded(false);

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    // Tracked so a second restart, or an unmount (destroy, mode switch) inside the
    // delay, cannot open a socket that no instance owns.
    if (restartTimerRef.current) {
      clearTimeout(restartTimerRef.current);
    }
    restartTimerRef.current = setTimeout(() => {
      restartTimerRef.current = null;
      initializeSocket();
    }, 500);
  }, [initializeSocket, resetConversationUiState, storage]);

  /**
   * Close chat window.
   * If an onClose callback is set and returns false, the default close is prevented.
   */
  const closeChat = useCallback(() => {
    if (embedded) {
      return;
    }
    if (callbacks?.onClose) {
      const result = callbacks.onClose();
      if (result === false) return;
    }
    storage.setUserClosed(true);
    setBubbleState('bubble');
  }, [callbacks, embedded, storage]);

  /**
   * End session: close window, clear all session data, disconnect.
   * A new session is only created when the chat bubble is clicked again.
   * Embedded mode has no closed state, so it starts a new session right away.
   * If an onEndSession callback is set and returns false, the default behavior is prevented.
   */
  const endSession = useCallback(() => {
    if (callbacks?.onEndSession) {
      const result = callbacks.onEndSession();
      if (result === false) return;
    }
    if (embedded) {
      clearSession();
      return;
    }
    storage.clearSession();
    sessionIdRef.current = null;

    resetConversationUiState();
    setSessionId(null);
    setIsSessionEnded(false);

    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setIsConnected(false);

    setBubbleState('bubble');
  }, [callbacks, embedded, clearSession, resetConversationUiState, storage]);

  /**
   * Open chat window.
   * If an onOpen callback is set and returns false, the open is prevented.
   */
  const openChat = useCallback(() => {
    if (embedded) {
      return;
    }
    if (callbacks?.onOpen) {
      const result = callbacks.onOpen();
      if (result === false) return;
    }
    storage.setUserClosed(false);
    proactiveShownRef.current = true;
    // bubble→open morphs via CSS (both sizes are explicit px). The proactive card
    // is auto-sized, so card→open can't be CSS-transitioned (auto→px doesn't
    // animate) — capture the card's current footprint so the layout effect below
    // can grow the shell from it with a WAAPI animation instead.
    const shell = shellRef.current;
    if (shell && shell.classList.contains('omq-chatbot-shell--card')) {
      const rect = shell.getBoundingClientRect();
      cardOpenStartSizeRef.current = { w: rect.width, h: rect.height };
    }
    setBubbleState('open');
  }, [callbacks, embedded, storage]);

  /**
   * Toggle chat window. Delegates to openChat/closeChat so callbacks and the
   * user-closed flag are applied consistently in both directions.
   */
  const toggleChat = useCallback(() => {
    if (isOpen) {
      closeChat();
    } else {
      openChat();
    }
  }, [isOpen, closeChat, openChat]);

  // Register imperative controls so external JS can open/close/toggle the chat
  useEffect(() => {
    if (controls) {
      controls.open = openChat;
      controls.close = closeChat;
      controls.toggle = toggleChat;
    }
  }, [controls, openChat, closeChat, toggleChat]);

  // Check for auto-open flag on mount
  useEffect(() => {
    if (storage.consumeAutoOpen() && !storage.isUserClosed()) {
      setBubbleState('open');
    }
  }, [storage]);

  // Proactive mode: after the backend-configured delay (chatConfig.proactiveDelayMs,
  // per chat with a server-wide default) without user interaction, morph the bubble
  // into the proactive greeting card. Timer is per-mount: every page load gets a fresh
  // countdown. If the user clicks the bubble first, the click handler clears this timer
  // via the proactiveShownRef guard.
  useEffect(() => {
    if (!chatConfig?.proactiveMode || isOpen || bubbleState !== 'bubble' || proactiveShownRef.current) {
      return;
    }
    const delayMs = resolveProactiveDelayMs(chatConfig.proactiveDelayMs);
    const timeoutId = setTimeout(() => {
      proactiveShownRef.current = true;
      // Capture the collapsed bubble's footprint so the card-entry effect can grow
      // the shell from the bubble size up to the measured final card size.
      const shell = shellRef.current;
      if (shell) {
        const rect = shell.getBoundingClientRect();
        proactiveStartSizeRef.current = { w: rect.width, h: rect.height };
      }
      setBubbleState('card');
    }, delayMs);
    return () => clearTimeout(timeoutId);
  }, [chatConfig?.proactiveMode, chatConfig?.proactiveDelayMs, bubbleState]);

  // Bubble → card: grow the shell from the bubble's footprint up to the measured
  // final card size (like the chat window opening), so the card expands instead of
  // popping in at full size. The card's auto width/height resolve to the final
  // size immediately in this layout effect, so we measure them and animate the box
  // from the captured bubble size. The content fades in over the latter half and
  // the sparkle spins in place — both via CSS in chatbot.less, timed to finish with
  // this grow. Skipped under reduced-motion (the card just snaps in). Expand only.
  useLayoutEffect(() => {
    if (bubbleState !== 'card') {
      return;
    }
    const start = proactiveStartSizeRef.current;
    proactiveStartSizeRef.current = null;
    // No grow animation (no captured footprint / reduced motion / no shell): the
    // card snaps in at full size, so the typewriter can start immediately.
    if (!start) {
      setProactiveTyping(true);
      return;
    }
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      setProactiveTyping(true);
      return;
    }
    const shell = shellRef.current;
    if (!shell) {
      setProactiveTyping(true);
      return;
    }
    // The card has no CSS geometry transition, so the box is already at its
    // settled size here — measure it as the grow target.
    const finalRect = shell.getBoundingClientRect();
    // Drop the card's min-width/height floors for the duration of the grow so the
    // box can animate down to the bubble footprint; the CSS floors are restored
    // when the animation finishes (the box is already at full size by then).
    shell.style.minWidth = '0px';
    shell.style.minHeight = '0px';
    const restoreMinSize = () => {
      shell.style.minWidth = '';
      shell.style.minHeight = '';
    };
    const anim = shell.animate(
      [
        { width: `${start.w}px`, height: `${start.h}px` },
        { width: `${finalRect.width}px`, height: `${finalRect.height}px` },
      ],
      // Matches the .omq-chatbot-shell--card surface transition (600ms) in
      // chatbot.less so the box, background fade, and content fade finish together.
      { duration: 600, easing: 'ease-out' },
    );
    anim.onfinish = () => {
      restoreMinSize();
      // Start the greeting typewriter only once the card has fully grown.
      setProactiveTyping(true);
    };
    return () => {
      anim.cancel();
      restoreMinSize();
    };
  }, [bubbleState]);

  // Reset the typewriter gate whenever we leave the proactive card (back to the
  // bubble, or on open) so re-entering the card replays the type-after-open flow.
  useEffect(() => {
    if (bubbleState !== 'card') {
      setProactiveTyping(false);
    }
  }, [bubbleState]);

  // Card → open: grow the shell from the proactive card's footprint up to the
  // measured open-window size, so opening from the proactive card morphs instead
  // of blinking. Needed because the card is auto-sized and CSS can't transition
  // width/height to/from `auto`; bubble→open is left to CSS (both sizes explicit).
  // Only runs when openChat captured a card footprint (cardOpenStartSizeRef).
  // Skipped under reduced-motion (the window just snaps in). Expand only.
  useLayoutEffect(() => {
    if (bubbleState !== 'open') {
      return;
    }
    const start = cardOpenStartSizeRef.current;
    cardOpenStartSizeRef.current = null;
    if (!start) {
      return;
    }
    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      return;
    }
    const shell = shellRef.current;
    if (!shell) {
      return;
    }
    // Suppress the shell's CSS geometry transition before measuring. Opening from
    // the card flips --card → --open, and --open inherits the base width/height
    // transition, so getBoundingClientRect would otherwise return the card width
    // still mid-transition (340px) instead of the open-window width — the box would
    // grow in height but stay card-width, then snap wide at the end. Also drop the
    // open min-width/height floors so the WAAPI can start from the smaller card
    // size. All are restored when the grow finishes; the WAAPI drives the morph.
    shell.style.transition = 'none';
    shell.style.minWidth = '0px';
    shell.style.minHeight = '0px';
    const finalRect = shell.getBoundingClientRect();
    const restoreMinSize = () => {
      shell.style.minWidth = '';
      shell.style.minHeight = '';
      shell.style.transition = '';
    };
    const anim = shell.animate(
      [
        { width: `${start.w}px`, height: `${start.h}px` },
        { width: `${finalRect.width}px`, height: `${finalRect.height}px` },
      ],
      // Matches the base .omq-chatbot-shell geometry transition (350ms) used for
      // bubble→open, so opening feels the same from the card or the bubble.
      { duration: 350, easing: 'ease-out' },
    );
    anim.onfinish = restoreMinSize;
    return () => {
      anim.cancel();
      restoreMinSize();
    };
  }, [bubbleState]);

  // Move keyboard focus with the open/close state: into the window when the
  // chat opens (the input takes over once connected — see ChatWindow), back to
  // the launcher when it closes (otherwise focus silently falls to <body>
  // when the focused element's subtree turns visibility:hidden).
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = isOpen;
    const shell = shellRef.current;
    if (!shell || wasOpen === isOpen) {
      return;
    }
    if (isOpen) {
      // ChatWindow's own effect may already have focused the text input
      // (desktop, connected) — only take over when focus is still outside.
      const win = shell.querySelector<HTMLElement>('.omq-chatbot-window');
      const active = (shell.getRootNode() as Document | ShadowRoot).activeElement;
      if (win && !win.contains(active)) {
        win.focus();
      }
    } else {
      shell.querySelector<HTMLElement>('.omq-chatbot-bubble-trigger')?.focus();
    }
  }, [isOpen]);

  // Fetch config (including relay URL and custom styles) on mount so styles
  // are applied before the user opens the chat window.
  useEffect(() => {
    if (!chatConfig) {
      fetchConfig();
    }
  }, [chatConfig, fetchConfig]);

  // Initialize socket once relay URL is available
  useEffect(() => {
    if ((!isOpen && !reconnectingRef.current) || !relayUrl) {
      return;
    }

    // Only initialize if not already connected
    if (socketRef.current) {
      return;
    }

    initializeSocket();
  }, [isOpen, relayUrl, initializeSocket]);

  // Reconnect / refresh activity when the tab becomes visible. iOS Safari may
  // suspend the page and silently kill the WebSocket; this triggers a recovery
  // faster than socket.io's own heartbeat timeout.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (!socket) return;
      if (!socket.connected) {
        socket.connect();
      } else {
        socket.emit('ping');
        renewStoredSession();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [renewStoredSession]);

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      pendingDelaysRef.current.forEach((timeout) => clearTimeout(timeout));
      if (keepaliveIntervalRef.current) {
        clearInterval(keepaliveIntervalRef.current);
        keepaliveIntervalRef.current = null;
      }
      if (restartTimerRef.current) {
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      // Remove the document-level <style> we injected for @font-face blocks
      document.getElementById(documentFontStyleId(account))?.remove();
    };
  }, [account]);

  // iOS Safari renders the host page's body in the strip between
  // visualViewport.bottom and the on-screen keyboard. The chatbot itself
  // cannot paint there (descendants of position:fixed containers don't
  // render past the container's bounds in this iOS quirk, even with no
  // overflow:hidden anywhere). The only reliable way to fill that strip
  // with the chatbot background is to mutate the host page's body
  // background while the chatbot is open on mobile, then restore on close.
  useEffect(() => {
    // Embedded: the chat is part of the page, not a fullscreen overlay.
    if (!isOpen || embedded) {
      return;
    }
    const mql = window.matchMedia('(max-width: 480px)');
    if (!mql.matches) {
      return;
    }

    // Read the chatbot's themed background by sampling the input wrapper's
    // computed backgroundColor — it always inherits --omq-background-color.
    // Fall back to white.
    let bgColor = '#ffffff';
    try {
      const wrapper = shadowRoot?.querySelector('.omq-chatbot-input-wrapper');
      if (wrapper) {
        const computed = window.getComputedStyle(wrapper).backgroundColor;
        if (computed && computed !== 'rgba(0, 0, 0, 0)' && computed !== 'transparent') {
          bgColor = computed;
        }
      }
    } catch {
      // fall back to white
    }

    // iOS Safari paints the canvas (html/body background) in the strip
    // between visualViewport.bottom and the keyboard. The chatbot cannot
    // paint there itself due to webkit clipping quirks inside position:fixed
    // subtrees. Mutating both <html> and <body> inline backgroundColor
    // covers all cases: the canvas takes its background from <html> if set,
    // else from <body>.
    const originalBodyColor = document.body.style.backgroundColor;
    const originalHtmlColor = document.documentElement.style.backgroundColor;

    // Apply the canvas background override only after the open morph finishes
    // (MOBILE_MASK_DELAY_MS), in step with the host-content mask below — so the
    // host page keeps its own background behind the growing window instead of
    // being recoloured at the start of the animation. Cleanup (on close, before
    // the timer fires, or after) clears the timer and restores the originals.
    const bgTimeoutId = setTimeout(() => {
      document.body.style.backgroundColor = bgColor;
      document.documentElement.style.backgroundColor = bgColor;
    }, MOBILE_MASK_DELAY_MS);

    return () => {
      clearTimeout(bgTimeoutId);
      document.body.style.backgroundColor = originalBodyColor;
      document.documentElement.style.backgroundColor = originalHtmlColor;
    };
  }, [isOpen, embedded, shadowRoot]);

  // Mask the host page's content while the chatbot is open on mobile.
  // iOS Safari renders body-level content in the strip below
  // visualViewport.bottom, where the chatbot itself cannot paint due to
  // webkit clipping quirks inside position:fixed subtrees. The body/html
  // backgroundColor override above handles the canvas, but body's own
  // children (text, divs, images) still render in that strip. Visibility-
  // hiding them ensures the strip shows only the canvas color (which we
  // already override to match the chatbot background).
  //
  // The chatbot is fullscreen on mobile, so the user can't see the host
  // page anyway — hiding its children is visually a no-op for them, with
  // the side effect of fixing the iOS gap.
  useEffect(() => {
    // Embedded: hiding the host page would hide the page the chat lives in.
    if (!isOpen || embedded) {
      return;
    }
    const mql = window.matchMedia('(max-width: 480px)');
    if (!mql.matches) {
      return;
    }

    const STYLE_ID = 'omq-chatbot-mobile-mask-host';
    const BODY_CLASS = 'omq-chatbot-mobile-active';

    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      styleEl.textContent =
        'body.' + BODY_CLASS + ' > *:not(#chatbot-container) { visibility: hidden !important; }';
      document.head.appendChild(styleEl);
    }

    // Apply the mask only after the open morph finishes (MOBILE_MASK_DELAY_MS).
    // Masking at the start of the animation hid the host page while the window
    // was still small, so the page appeared to blink out before the window
    // covered it. On close, isOpen flips false → cleanup clears the pending
    // timer and removes the class, so the host page reappears immediately.
    const maskTimeoutId = setTimeout(() => {
      document.body.classList.add(BODY_CLASS);
    }, MOBILE_MASK_DELAY_MS);

    return () => {
      clearTimeout(maskTimeoutId);
      document.body.classList.remove(BODY_CLASS);
    };
  }, [isOpen, embedded]);

  // Don't render until config (including custom style overrides) has been fetched
  // to avoid a flash of unstyled/default-styled content.
  if (!configReady) {
    return null;
  }

  // Embedded carries no position class: those anchor the fixed container to a
  // viewport corner and switch it to fullscreen on mobile.
  const containerModeClass = embedded
    ? 'omq-chatbot-embedded'
    : `omq-chatbot-position-${effectivePosition}`;

  return (
    <div
      className={`omq-chatbot-container ${containerModeClass}${isOpen ? ' omq-chatbot-open' : ''}`}
      lang={chatConfig?.language}>
      <div ref={shellRef} className={`omq-chatbot-shell omq-chatbot-shell--${bubbleState}`}>
        {!embedded && (
          <ChatBubble
            onClick={toggleChat}
            proactive={chatConfig?.proactiveMode}
            active={proactiveTyping}
            title={bubbleTitle}
            isOpen={isOpen}
            labels={chatConfig?.messages}
            greetings={
              ((chatConfig?.messages as unknown as { proactiveGreetings?: string[] })
                ?.proactiveGreetings) ?? []
            }
          />
        )}
        <ChatWindow
          account={account}
          apiKey={apiKey}
          isOpen={isOpen}
          embedded={embedded}
          isConnected={isConnected}
          isTyping={isTyping}
          isSessionEnded={isSessionEnded}
          isUploading={isUploading}
          messages={messages}
          quickResponses={quickResponses}
          onClose={closeChat}
          onSendMessage={sendMessage}
          onQuickResponse={handleQuickResponse}
          onClearSession={clearSession}
          onEndSession={endSession}
          onNavigate={handleNavigate}
          activityText={activityText}
          uploadEnabled={uploadEnabled}
          uploadAccept={uploadAccept}
          uploadMultiple={uploadMultiple}
          uploadValidateError={uploadValidateError}
          multiSelectOptions={multiSelectOptions}
          multiSelectMinSelect={multiSelectMinMax.min}
          multiSelectMaxSelect={multiSelectMinMax.max}
          multiSelectValidateErrorMessage={multiSelectValidateErrorMessage}
          onMultiSelectConfirm={handleMultiSelectConfirm}
          inputFieldDescription={inputFieldDescription}
          inputFieldPlaceholder={inputFieldPlaceholder}
          multiSelectDescription={multiSelectDescription}
          quickResponsesDescription={quickResponsesDescription}
          uploadDescription={uploadDescription}
          logoUrl={chatConfig?.logoUrl}
          botName={chatConfig?.botName}
          labels={chatConfig?.messages}
          inputPlaceholder={chatConfig?.messages?.inputPlaceholder}
          offlineWarning={chatConfig?.messages?.offlineWarning}
          sessionEndedMessage={chatConfig?.messages?.sessionEnded as string}
          lang={chatConfig?.language}
        />
      </div>
    </div>
  );
};
